/**
 * Individual schedule line row component.
 *
 * Renders dynamic column cells based on visibility configuration,
 * with status badges, per-row action buttons, and selection checkbox.
 */
import { Check, XCircle, Edit, Pause, Play, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import {
  ScheduleColumnConfig,
  ACK_STATUS_CONFIG,
  SCHEDULE_STATUS_CONFIG,
} from './types';

interface ScheduleLineRowProps {
  schedule: POLineSchedule;
  visibleColumns: ScheduleColumnConfig[];
  formatDate: (date: string | Date) => string;
  formatNumber: (value: number) => string;
  onOpenModal: (kind: string, schedule: POLineSchedule) => void;
  onRowClick?: (scheduleId: number) => void;
  isSelected: boolean;
  canSelectRow: boolean;
  onToggleSelect: (id: number) => void;
  isCustomer: boolean;
}

/** A schedule is "closed" once it's fully shipped or cancelled — no further
 *  collaboration is meaningful and the row should render as a read-only record. */
function isClosed(s: POLineSchedule): boolean {
  return s.status === 'shipped' || s.status === 'cancelled';
}

/** Whether a schedule row should show accept/decline actions. */
function canAct(s: POLineSchedule, isCustomer: boolean): boolean {
  if (isClosed(s)) return false;
  if (isCustomer) {
    const actionable = ['new', 'change_proposed_supplier'];
    return !!s.acknowledge_status && actionable.includes(s.acknowledge_status);
  }
  // Supplier side: can accept counter-proposals from customer
  const supplierActionable = ['change_proposed_customer'];
  return !!s.acknowledge_status && supplierActionable.includes(s.acknowledge_status);
}

/** Whether a schedule row should show the propose change action. */
function canPropose(s: POLineSchedule, isCustomer: boolean): boolean {
  if (isClosed(s)) return false;
  if (isCustomer) {
    const proposable = ['new', 'accepted', 'wip', 'change_proposed_supplier'];
    return !!s.acknowledge_status && proposable.includes(s.acknowledge_status);
  }
  // Supplier side
  const supplierProposable = ['new', 'accepted', 'wip', 'change_proposed_customer'];
  return !!s.acknowledge_status && supplierProposable.includes(s.acknowledge_status);
}

function renderAckBadge(s: POLineSchedule) {
  // Closed schedules show a "Closed" ack badge regardless of the stored
  // acknowledge_status — the row is no longer actionable and the "New" pill
  // misleads the user into thinking a response is expected.
  if (isClosed(s)) {
    const config = ACK_STATUS_CONFIG['closed'] || { label: 'Closed', className: 'bg-gray-100 text-gray-500' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
  }
  const status = s.acknowledge_status || 'new';
  const config = ACK_STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  if (s.on_hold) {
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">On Hold</span>;
  }
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
}

function renderStatusBadge(s: POLineSchedule) {
  const config = SCHEDULE_STATUS_CONFIG[s.status] || { label: s.status, className: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
}

function renderWaitingOn(s: POLineSchedule) {
  if (!s.waiting_on) return <span className="text-muted-foreground text-xs">-</span>;
  const cls = s.waiting_on === 'Customer' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800';
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s.waiting_on}</span>;
}

/**
 * Renders a single schedule line table row with all visible columns.
 */
export function ScheduleLineRow({
  schedule: s,
  visibleColumns,
  formatDate,
  formatNumber,
  onOpenModal,
  onRowClick,
  isSelected,
  canSelectRow,
  onToggleSelect,
  isCustomer,
}: ScheduleLineRowProps) {
  const renderCell = (colId: string) => {
    switch (colId) {
      case 'acknowledge_status':
        return (
          <td key={colId} className="py-3 px-4">
            {s.is_forecast ? (
              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium" title={`Forecast — outside firm window (${s.firm_days ?? '?'} days)`}>
                Forecast
              </span>
            ) : (
              renderAckBadge(s)
            )}
          </td>
        );
      case 'action':
        return (
          <td key={colId} className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1">
              {canAct(s, isCustomer) && (
                <>
                  <button onClick={() => onOpenModal('accept', s)} className="p-1 hover:bg-green-50 rounded" title="Accept"><Check className="h-4 w-4 text-green-600" /></button>
                  <button onClick={() => onOpenModal('decline', s)} className="p-1 hover:bg-red-50 rounded" title="Decline"><XCircle className="h-4 w-4 text-red-600" /></button>
                </>
              )}
              {canPropose(s, isCustomer) && (
                <button onClick={() => onOpenModal('propose_date', s)} className="p-1 hover:bg-blue-50 rounded" title="Propose Change"><Edit className="h-4 w-4 text-blue-600" /></button>
              )}
              {!isClosed(s) && (
                s.on_hold ? (
                  <button onClick={() => onOpenModal('release', s)} className="p-1 hover:bg-green-50 rounded" title="Release Hold"><Play className="h-4 w-4 text-green-600" /></button>
                ) : (
                  <button onClick={() => onOpenModal('hold', s)} className="p-1 hover:bg-orange-50 rounded" title="Put On Hold"><Pause className="h-4 w-4 text-orange-600" /></button>
                )
              )}
              {(isClosed(s) || (!canAct(s, isCustomer) && !canPropose(s, isCustomer) && !s.on_hold)) && (
                <span className="text-xs text-muted-foreground">-</span>
              )}
            </div>
          </td>
        );
      case 'on_hold':
        return (
          <td key={colId} className="py-3 px-4 text-center">
            {s.on_hold
              ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">On Hold</span>
              : <span className="text-muted-foreground text-xs">-</span>}
          </td>
        );
      case 'waiting_on':
        return <td key={colId} className="py-3 px-4 text-center">{renderWaitingOn(s)}</td>;
      case 'po_number':
        return <td key={colId} className="py-3 px-4"><span className="font-medium text-sm">{s.po_number}</span></td>;
      case 'line_number':
        return <td key={colId} className="py-3 px-4 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{s.line_number}</span></td>;
      case 'schedule_line_number':
        return <td key={colId} className="py-3 px-4 text-center text-sm">{s.schedule_line_number || '-'}</td>;
      case 'part_number':
        return <td key={colId} className="py-3 px-4"><span className="font-mono text-sm font-medium">{s.part_number}</span></td>;
      case 'description':
        return <td key={colId} className="py-3 px-4"><div className="text-sm">{s.description}</div></td>;
      case 'customer_name':
        return (
          <td key={colId} className="py-3 px-4 text-sm">
            {s.customer_id ? (
              <Link
                to={`/customers/${s.customer_id}`}
                className="text-primary hover:underline inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {s.customer_name || 'N/A'}
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              s.customer_name || 'N/A'
            )}
          </td>
        );
      case 'warehouse':
        return <td key={colId} className="py-3 px-4 text-sm">{s.warehouse || '—'}</td>;
      case 'supplier_name':
        return <td key={colId} className="py-3 px-4 text-sm">{s.supplier_name || 'N/A'}</td>;
      case 'schedule_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.schedule_date ? formatDate(s.schedule_date) : '-'}</td>;
      case 'supplier_promise_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.supplier_promise_date ? formatDate(s.supplier_promise_date) : '-'}</td>;
      case 'original_promise_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.original_promise_date ? formatDate(s.original_promise_date) : '-'}</td>;
      case 'schedule_qty':
        return <td key={colId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatNumber(parseFloat(s.schedule_qty))}</span></td>;
      case 'shipped_qty':
        return <td key={colId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatNumber(parseFloat(s.shipped_qty))}</span></td>;
      case 'balance_qty': {
        const bal = parseFloat(s.balance_qty);
        return <td key={colId} className="py-3 px-4 text-right"><span className={`text-sm font-medium ${bal > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>{formatNumber(bal)}</span></td>;
      }
      case 'unit_of_measure':
        return <td key={colId} className="py-3 px-4 text-sm text-center"><span className="px-2 py-1 bg-muted rounded text-xs font-medium">{s.unit_of_measure || '-'}</span></td>;
      case 'unit_price':
        return <td key={colId} className="py-3 px-4 text-right text-sm">{s.unit_price ? parseFloat(s.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : '-'}</td>;
      case 'status':
        return <td key={colId} className="py-3 px-4">{renderStatusBadge(s)}</td>;
      case 'updated_at':
        return <td key={colId} className="py-3 px-4 text-sm">{s.updated_at ? formatDate(s.updated_at) : '-'}</td>;
      default:
        return null;
    }
  };

  const closed = isClosed(s);
  const forecast = !!s.is_forecast;
  return (
    <tr
      className={`border-b hover:bg-muted/50 transition-colors cursor-pointer ${s.on_hold ? 'bg-red-50/50' : ''} ${closed ? 'opacity-60 bg-muted/40' : ''} ${forecast ? 'bg-amber-50/50' : ''}`}
      onClick={() => onRowClick?.(s.id)}
      title={forecast ? `Forecast — outside firm window (${s.firm_days ?? '?'} days)` : undefined}
    >
      <td className="py-2 px-3 sticky left-0 bg-background z-10" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(s.id)}
          disabled={!canSelectRow}
          className="h-4 w-4 rounded border-gray-300"
        />
      </td>
      {visibleColumns.map((col) => renderCell(col.id))}
    </tr>
  );
}
